/**
 * Mock SAM.gov Data Generator
 *
 * Generates realistic sample opportunities for testing the UI
 * while waiting for a valid SAM.gov Opportunities API key.
 *
 * IMPORTANT: This data structure MUST match exactly what the Cloud Function
 * stores when ingesting from the real SAM.gov API (see functions/index.js)
 */

const AGENCIES = [
  { department: "DEPT OF DEFENSE", sub: "DEPT OF THE ARMY", office: "Army Contracting Command" },
  { department: "DEPT OF DEFENSE", sub: "DEPT OF THE NAVY", office: "Naval Sea Systems Command" },
  { department: "DEPT OF DEFENSE", sub: "DEPT OF THE AIR FORCE", office: "Air Force Materiel Command" },
  { department: "DEPT OF HOMELAND SECURITY", sub: "CUSTOMS AND BORDER PROTECTION", office: "Procurement Directorate" },
  { department: "DEPT OF HEALTH AND HUMAN SERVICES", sub: "CENTERS FOR DISEASE CONTROL", office: "Office of Acquisition Services" },
  { department: "DEPT OF VETERANS AFFAIRS", sub: "VA HEALTH ADMINISTRATION", office: "Network Contracting Office" },
  { department: "GENERAL SERVICES ADMINISTRATION", sub: "FEDERAL ACQUISITION SERVICE", office: "IT Acquisition Center" },
  { department: "DEPT OF JUSTICE", sub: "FEDERAL BUREAU OF INVESTIGATION", office: "Procurement Section" },
  { department: "DEPT OF ENERGY", sub: "NATIONAL NUCLEAR SECURITY ADMIN", office: "Office of Acquisition Management" },
  { department: "NATIONAL AERONAUTICS AND SPACE ADMIN", sub: "NASA HEADQUARTERS", office: "Office of Procurement" },
];

// NAICS codes commonly used in IT/Professional Services
const NAICS_CODES = [
  "541511", // Custom Computer Programming Services
  "541512", // Computer Systems Design Services
  "541513", // Computer Facilities Management Services
  "541519", // Other Computer Related Services
  "541611", // Administrative Management Consulting Services
  "541612", // Human Resources Consulting Services
  "541613", // Marketing Consulting Services
  "541614", // Process & Logistics Consulting Services
  "541618", // Other Management Consulting Services
  "541690", // Other Scientific & Technical Consulting Services
  "541715", // R&D in Physical, Engineering, and Life Sciences
  "518210", // Data Processing, Hosting, and Related Services
  "611430", // Professional and Management Development Training
  "561330", // Professional Employer Organizations
  "561320", // Temporary Help Services
  "561210", // Facilities Support Services
  "561110", // Office Administrative Services
];

const TITLES = [
  "Enterprise IT Modernization Support Services",
  "Cybersecurity Operations and Monitoring Services",
  "Cloud Migration and Infrastructure Services",
  "Software Development and Maintenance Support",
  "Data Analytics and Business Intelligence Platform",
  "Network Infrastructure Upgrade and Support",
  "Help Desk and End User Support Services",
  "Application Development and DevSecOps Services",
  "Systems Integration and Engineering Support",
  "Digital Transformation Consulting Services",
  "AI/ML Platform Development and Support",
  "Zero Trust Architecture Implementation",
  "Managed Security Services Provider (MSSP)",
  "Enterprise Resource Planning (ERP) Support",
  "Identity and Access Management Services",
  "IT Program Management Support",
  "Database Administration and Optimization",
  "Quality Assurance and Testing Services",
  "Technical Writing and Documentation Services",
  "Training and Knowledge Management Services",
];

// Set-asides matching SAM.gov API codes - includes ALL veteran types
const SET_ASIDES = [
  { code: "SBA", description: "Total Small Business Set-Aside" },
  { code: "8A", description: "8(a) Set-Aside" },
  { code: "SDVOSBC", description: "Service-Disabled Veteran-Owned Small Business (Sole Source)" },
  { code: "SDVOSB", description: "Service-Disabled Veteran-Owned Small Business" },
  { code: "VSB", description: "Veteran Small Business Set-Aside" },
  { code: "VOSB", description: "Veteran-Owned Small Business Set-Aside" },
  { code: "WOSB", description: "Women-Owned Small Business" },
  { code: "EDWOSB", description: "Economically Disadvantaged Women-Owned Small Business" },
  { code: "HUBZone", description: "HUBZone Set-Aside" },
  { code: null, description: "Full and Open Competition" },
];

// Opportunity types matching SAM.gov API
const OPPORTUNITY_TYPES = [
  { code: "o", baseType: "Solicitation" },
  { code: "p", baseType: "Presolicitation" },
  { code: "k", baseType: "Combined Synopsis/Solicitation" },
  { code: "r", baseType: "Sources Sought" },
];

// Cities for place of performance
const LOCATIONS = [
  { city: "Washington", state: "DC", zip: "20001" },
  { city: "Arlington", state: "VA", zip: "22201" },
  { city: "San Antonio", state: "TX", zip: "78201" },
  { city: "Colorado Springs", state: "CO", zip: "80901" },
  { city: "Huntsville", state: "AL", zip: "35801" },
  { city: "San Diego", state: "CA", zip: "92101" },
  { city: "Norfolk", state: "VA", zip: "23501" },
  { city: "Fort Worth", state: "TX", zip: "76101" },
];

/**
 * Generate a random date within a range
 */
function randomDate(daysAgo, daysAhead = 0) {
  const now = new Date();
  const start = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
  const end = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
}

/**
 * Generate a random notice ID (12 characters like SAM.gov)
 */
function generateNoticeId() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < 32; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

/**
 * Generate a random solicitation number
 */
function generateSolicitationNumber() {
  const prefixes = ["W911", "N001", "FA87", "HSHQ", "75D3", "36C2", "47QS", "15JP", "DE-S", "NNH2"];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const year = new Date().getFullYear().toString().slice(-2);
  const seq = Math.floor(Math.random() * 9000 + 1000);
  return `${prefix}${year}-R-${seq}`;
}

/**
 * Generate a single mock opportunity
 * Structure matches exactly what functions/index.js stores from SAM.gov API
 */
export function generateMockOpportunity(naicsCode = null, index = 0) {
  const agency = AGENCIES[Math.floor(Math.random() * AGENCIES.length)];
  const title = TITLES[Math.floor(Math.random() * TITLES.length)];
  const setAside = SET_ASIDES[Math.floor(Math.random() * SET_ASIDES.length)];
  const type = OPPORTUNITY_TYPES[Math.floor(Math.random() * OPPORTUNITY_TYPES.length)];
  const location = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];

  // Use provided NAICS or pick random one
  const naics = naicsCode || NAICS_CODES[Math.floor(Math.random() * NAICS_CODES.length)];

  const postedDate = randomDate(30, 0);
  const responseDeadline = randomDate(-5, 45); // 5 days ago to 45 days ahead
  const archiveDate = new Date(responseDeadline.getTime() + 30 * 24 * 60 * 60 * 1000);

  const noticeId = generateNoticeId();
  const solicitationNumber = generateSolicitationNumber();

  // Contact info
  const contactName = `Contract Specialist ${index + 1}`;
  const contactEmail = `contracts@${agency.department.toLowerCase().replace(/\s+/g, "").slice(0, 10)}.gov`;
  const contactPhone = `(202) 555-${Math.floor(Math.random() * 9000 + 1000)}`;

  // Build the opportunity object matching API structure exactly
  return {
    // Identity - matches functions/index.js lines 332-336
    id: noticeId,
    notice_id: noticeId,
    solicitation_number: solicitationNumber,
    title: `${title} - ${agency.sub}`,

    // Classification - matches functions/index.js lines 339-343
    naics_code: naics,
    naics_codes: [naics],
    psc_code: `D${Math.floor(Math.random() * 400 + 300)}`,
    set_aside: setAside.code,
    set_aside_description: setAside.description,

    // Agency - matches functions/index.js lines 346-351
    department_name: agency.department,
    sub_tier_name: agency.sub,
    office_name: agency.office,
    full_parent_path: `${agency.department}.${agency.sub}.${agency.office}`,
    organization_type: "OFFICE",

    // Dates - matches functions/index.js lines 353-355
    posted_date: postedDate,
    response_deadline: responseDeadline,
    archive_date: archiveDate,

    // Type - matches functions/index.js lines 358-359
    opportunity_type: type.code,
    base_type: type.baseType,

    // Description - matches functions/index.js lines 362-363
    description: `The ${agency.sub} requires contractor support for ${title.toLowerCase()}. This procurement will provide comprehensive services including planning, implementation, and ongoing support. The contractor shall provide qualified personnel with relevant experience in government IT systems and security requirements. Period of performance is anticipated to be one base year with four option years. All work shall be performed in accordance with applicable federal regulations and agency policies.`,
    description_link: `https://api.sam.gov/prod/opportunities/v2/desc/${noticeId}`,

    // Links - matches functions/index.js lines 366-369
    ui_link: `https://sam.gov/opp/${noticeId}/view`,
    sam_url: `https://sam.gov/opp/${noticeId}/view`,
    additional_info_link: null,
    resource_links: [],

    // Contact - matches functions/index.js lines 372-373
    primary_contact: {
      fullName: contactName,
      email: contactEmail,
      phone: contactPhone,
      type: "primary",
    },
    all_contacts: [{
      fullName: contactName,
      email: contactEmail,
      phone: contactPhone,
      type: "primary",
    }],

    // Location - matches functions/index.js lines 376-377
    office_address: {
      city: location.city,
      state: location.state,
      zipcode: location.zip,
    },
    place_of_performance: {
      city: { name: location.city },
      state: { code: location.state },
      country: { code: "USA" },
      zip: location.zip,
    },

    // Award Info - matches functions/index.js line 380
    award: null,

    // Metadata - matches functions/index.js lines 383-386
    is_active: responseDeadline > new Date(),
    source: "mock-data",
    fetched_at: new Date(),
    updated_at: new Date(),
  };
}

/**
 * Generate multiple mock opportunities with good distribution of set-asides
 */
export function generateMockOpportunities(naicsCodes = [], count = 25) {
  const opportunities = [];

  // Ensure at least some veteran set-asides are included
  const veteranSetAsides = SET_ASIDES.filter(s =>
    s.code && (s.code.includes("SDVOS") || s.code.includes("VSB") || s.code.includes("VOSB"))
  );

  for (let i = 0; i < count; i++) {
    // Cycle through NAICS codes if provided
    const naics = naicsCodes.length > 0
      ? naicsCodes[i % naicsCodes.length]
      : null;

    const opp = generateMockOpportunity(naics, i);

    // Ensure ~40% of opportunities have veteran set-asides for testing
    if (i % 3 === 0 && veteranSetAsides.length > 0) {
      const vetSetAside = veteranSetAsides[i % veteranSetAsides.length];
      opp.set_aside = vetSetAside.code;
      opp.set_aside_description = vetSetAside.description;
    }

    opportunities.push(opp);
  }

  // Sort by posted date descending
  opportunities.sort((a, b) => b.posted_date - a.posted_date);

  return opportunities;
}

/**
 * Generate mock historical awards for similarity testing
 */
export function generateMockHistoricalAwards(naicsCodes = [], count = 50) {
  const awards = [];
  const vendors = [
    "Booz Allen Hamilton",
    "Leidos Holdings",
    "SAIC",
    "ManTech International",
    "Peraton",
    "CACI International",
    "General Dynamics IT",
    "Accenture Federal",
    "Deloitte Consulting",
    "IBM Federal",
  ];

  for (let i = 0; i < count; i++) {
    const agency = AGENCIES[Math.floor(Math.random() * AGENCIES.length)];
    const naics = naicsCodes.length > 0
      ? naicsCodes[i % naicsCodes.length]
      : NAICS_CODES[Math.floor(Math.random() * NAICS_CODES.length)];

    const startDate = randomDate(365 * 3, -30); // 3 years ago to 30 days ago
    const awardAmount = Math.floor(Math.random() * 50000000 + 500000); // $500K to $50M

    awards.push({
      piid: `${["W911", "N001", "FA87", "HSHQ"][Math.floor(Math.random() * 4)]}${Math.floor(Math.random() * 90 + 10)}-C-${Math.floor(Math.random() * 9000 + 1000)}`,
      award_id: `AWARD-${i + 1}`,
      naics_code: naics,
      psc_code: `D${Math.floor(Math.random() * 400 + 300)}`,
      agency_name: agency.department,
      sub_agency_name: agency.sub,
      office_name: agency.office,
      vendor_name: vendors[Math.floor(Math.random() * vendors.length)],
      award_amount: awardAmount,
      start_date: startDate,
      end_date: new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000 * (Math.floor(Math.random() * 4) + 1)),
      description: `Contract for IT services and support to ${agency.sub}`,
      source: "mock-data",
    });
  }

  return awards;
}

const mockDataExports = {
  generateMockOpportunity,
  generateMockOpportunities,
  generateMockHistoricalAwards,
  NAICS_CODES,
  SET_ASIDES,
};

export default mockDataExports;
